import type { ComponentType } from 'react';

import { CodeExecConfig } from './CodeExecConfig';
import { CompiledAgentConfig } from './CompiledAgentConfig';
import { LlmAgentConfig } from './LlmAgentConfig';
import { LockedFieldset } from './LockedFieldset';
import { McpQueryConfig } from './McpQueryConfig';
import { PassthroughConfig } from './PassthroughConfig';
import { RagRetrieveConfig } from './RagRetrieveConfig';
import { RawJsonConfig } from './RawJsonConfig';
import { TemplateRenderConfig } from './TemplateRenderConfig';
import { ThresholdCheckConfig } from './ThresholdCheckConfig';

/**
 * Props every per-node-type config form must accept. Forms write into the
 * node's `config` by calling `onChange` with a patch (shallow-merged by the
 * inspector); the JSON fallback receives the full replacement instead via
 * `onReplace` on the dispatcher. Form authors should declare their prop
 * type as `INodeConfigProps` so the registry stays type-safe — drop the
 * component into `nodeConfigRegistry` below to wire it up.
 */
export interface INodeConfigProps {
  config: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  /**
   * The selected node's key. Forms can use this to look up sibling fields
   * (e.g. the LLM-agent form reads the current `inputMapping` to compute
   * which `{{tokens}}` will resolve at runtime). Optional so older forms
   * that don't need it stay untouched.
   */
  nodeKey?: string;
  /**
   * Config field names the node's managed-workflow template owns (read-only
   * here; the API refuses a change with 409). `*` = the whole config, which
   * the dispatcher handles for every form. Absent / empty for user-owned
   * workflows. (DEV-17471.)
   */
  lockedFields?: ReadonlySet<string>;
}

/**
 * Registry of schema-aware editors keyed by node type. Add a new entry to
 * give a node type its own form; everything not in the map falls through to
 * `RawJsonConfig` so unknown / custom node types still save. The
 * `ComponentType<INodeConfigProps>` constraint enforces the contract at the
 * type level — TS rejects forms whose props don't match.
 */
export const nodeConfigRegistry: Record<string, ComponentType<INodeConfigProps>> = {
  'code-exec': CodeExecConfig,
  // compiler output target — read-only viewer, not a raw-JSON editor.
  'compiled-agent': CompiledAgentConfig,
  'llm-agent': LlmAgentConfig,
  'mcp-query': McpQueryConfig,
  passthrough: PassthroughConfig,
  'rag-retrieve': RagRetrieveConfig,
  'template-render': TemplateRenderConfig,
  'threshold-check': ThresholdCheckConfig,
};

/**
 * Dispatcher used by the workflow editor's inspector. Looks up the
 * registered form for `nodeType`; renders `RawJsonConfig` when no entry
 * exists so unknown types remain editable instead of being unreachable.
 *
 * The fallback path passes the full replacement object, the registry path
 * passes a patch — both are accepted by the inspector caller, which decides
 * how to merge. (Keeping the dispatch this thin makes it trivial to test:
 * pick a node type, assert which component renders.)
 */
export function NodeConfigEditor({
  nodeType,
  nodeKey,
  config,
  onChange,
  onReplace,
  lockedFields,
}: {
  nodeType: string;
  nodeKey?: string;
  config: Record<string, unknown>;
  /** Shallow-merge patch — used by registered forms. */
  onChange: (patch: Record<string, unknown>) => void;
  /** Full replacement — used by the JSON fallback. */
  onReplace: (next: Record<string, unknown>) => void;
  /** Read-only config fields of a managed-workflow node; `*` locks the whole config. */
  lockedFields?: ReadonlySet<string>;
}) {
  const Registered = nodeConfigRegistry[nodeType];
  // Whole-config lock: no form of any node type may write, so the gate lives
  // here rather than in each form.
  const allLocked = lockedFields?.has('*') === true;
  if (Registered) {
    return (
      <LockedFieldset locked={allLocked}>
        <Registered
          config={config}
          onChange={allLocked ? ignoreChange : onChange}
          nodeKey={nodeKey}
          lockedFields={lockedFields}
        />
      </LockedFieldset>
    );
  }
  return <RawJsonConfig config={config} onChange={onReplace} readOnly={allLocked} />;
}

function ignoreChange(): void {
  // Locked config — edits are dropped.
}

/** Returns true when a node type has a registered schema-aware editor. */
export function hasNodeConfigForm(nodeType: string): boolean {
  return Object.prototype.hasOwnProperty.call(nodeConfigRegistry, nodeType);
}
